import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { RouterOutlet, Router } from '@angular/router';
import { NavigationService } from './services/navigation.service';
import { DataService } from './services/data.service';
import { AuthService } from './services/auth.service';
import { ToastNotificationComponent } from './components/toast-notification.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    ToastNotificationComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Toast Notification - Global -->
    <app-toast-notification />

    <!-- Show login page or app layout based on auth state -->
    @if (!isLoginPage()) {
      <div class="min-h-screen bg-gray-50">
        <!-- Sidebar -->
        <aside [class]="'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ' + (navService.sidebarOpen() ? 'translate-x-0' : '-translate-x-full')">
          <div class="flex flex-col h-full">
            <!-- Logo -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div class="flex items-center space-x-3">
                <img
                  src="viralwave-logo.jpg"
                  alt="ViralWave Logo"
                  class="h-10 w-auto object-contain"
                />
              </div>
              <button
                (click)="navService.closeSidebar()"
                class="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <!-- Navigation -->
            <nav class="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
              @for (item of navService.navigation; track item.id) {
                <button
                  (click)="navigateTo(item.id)"
                  [class]="'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ' + (isActiveRoute(item.id) ? 'bg-gradient-to-r from-[#FF4545] to-[#FAA030] text-white' : 'text-gray-700 hover:bg-gray-100')"
                >
                  <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <!-- Dashboard Icon -->
                    @if (item.icon === 'layout-dashboard') {
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                    }
                    <!-- File Text Icon -->
                    @if (item.icon === 'file-text') {
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    }
                    <!-- Dollar Sign Icon -->
                    @if (item.icon === 'dollar-sign') {
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    }
                    <!-- Trending Up Icon -->
                    @if (item.icon === 'trending-up') {
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                    }
                    <!-- Bell Icon -->
                    @if (item.icon === 'bell') {
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                    }
                    <!-- Message Square Icon -->
                    @if (item.icon === 'message-square') {
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                    }
                    <!-- Settings Icon -->
                    @if (item.icon === 'settings') {
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    }
                  </svg>
                  <span class="font-medium text-sm leading-snug text-left">{{ item.name }}</span>
                </button>
              }
            </nav>

            <!-- User Profile -->
            <div class="px-4 py-4 border-t border-gray-200">
              <div class="flex items-center space-x-3 px-2 py-2">
                <div class="w-10 h-10 bg-gradient-to-r from-[#FF4545] to-[#FAA030] rounded-full flex items-center justify-center text-white font-semibold">
                  {{ getUserInitials() }}
                </div>
                <div class="flex-1">
                  <p class="text-sm font-semibold text-gray-900">{{ authService.currentUser()?.name || 'Guest' }}</p>
                  <p class="text-xs text-gray-500">{{ authService.currentUser()?.role === 'artist' ? 'Artist' : 'Manager' }}</p>
                </div>
                <button (click)="logout()" class="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Logout">
                  <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </aside>

        <!-- Mobile overlay -->
        @if (navService.sidebarOpen()) {
          <div
            class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            (click)="navService.closeSidebar()"
          ></div>
        }

        <!-- Main Content -->
        <div class="lg:pl-64">
          <!-- Top Bar -->
          <header class="sticky top-0 z-30 bg-white border-b border-gray-200 px-6 py-4">
            <div class="flex items-center justify-between">
              <button
                (click)="navService.toggleSidebar()"
                class="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
                </svg>
              </button>

              <div class="flex items-center space-x-4 ml-auto">
                <div class="relative p-2 hover:bg-white rounded-lg transition-colors">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                  </svg>
                  <span class="absolute top-1 right-1 w-2 h-2 bg-[#ffffff] rounded-full"></span>
                </div>
                <div class="relative p-2 hover:bg-white rounded-lg transition-colors">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                  </svg>
                  <span class="absolute top-1 right-1 w-2 h-2 bg-[#ffffff] rounded-full"></span>
                </div>
              </div>
            </div>
          </header>

          <!-- Page Content with Router -->
          <main class="p-6 lg:p-8">
            <router-outlet></router-outlet>
          </main>
        </div>
      </div>
    } @else {
      <router-outlet></router-outlet>
    }
  `
})
export class AppComponent {
  navService = inject(NavigationService);
  dataService = inject(DataService);
  authService = inject(AuthService);
  private router = inject(Router);

  isLoginPage(): boolean {
    const url = this.router.url;
    return url === '/login' || url === '/' || url.startsWith('/login');
  }

  isActiveRoute(route: string): boolean {
    const urlWithoutParams = this.router.url.split('?')[0];
    return urlWithoutParams === `/${route}`;
  }

  navigateTo(route: string): void {
    this.router.navigate([`/${route}`]);
    this.navService.closeSidebar();
  }

  logout(): void {
    this.authService.logout();
  }

  getUserInitials(): string {
    const user = this.authService.currentUser();
    if (!user || !user.name) return 'U';

    const names = user.name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  }
}
